import React, { useMemo } from 'react'
import { Card, CardContent, useTheme } from '@mui/material'
import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { barColors } from '../constants'
import { type KeyStatSurveyItem } from '../../../../models/Report'

export interface BarChartProps {
  titleComponent: React.ReactNode
  statisticBox: React.ReactNode
  chartKey: number
  showPercents?: boolean
  titles?: string[]
  report?: KeyStatSurveyItem
}

const BarChart: React.FC<BarChartProps> = ({
  titleComponent,
  statisticBox,
  chartKey,
  showPercents = false,
  titles = [],
  report
}) => {
  const theme = useTheme()

  const optionsData = useMemo(() => {
    const options = report?.options ?? []
    const keys = options.map(option => option.name)
    const values = options.map(option => option.counts)
    const percentages = options.map(option => option.percents)
    const dataSource = showPercents ? percentages : values

    // Helper to get actual value and percent for a given data point
    const getValueAndPercent = (dataPointIndex: number, seriesIndex: number): { value: number | null | undefined, percent: number | null | undefined } => {
      const value = values[dataPointIndex]?.[seriesIndex]
      const percent = percentages[dataPointIndex]?.[seriesIndex]
      return { value, percent }
    }

    // Checks if all items in the data source have a `null` or `0` value for the specified group index.
    const isGroupNull = (groupIndex: number): boolean =>
      dataSource.every(item => item?.[groupIndex] === null || item?.[groupIndex] === 0)

    const series = dataSource[0]?.map((_, groupIndex) => {
      const isNull = isGroupNull(groupIndex)

      return {
        name: titles[groupIndex] || `Group ${groupIndex++}`,
        data: isNull
          ? [] // Hide the series by providing an empty data array
          : dataSource.map(item =>
            item?.[groupIndex] !== null
              ? (item?.[groupIndex] ?? 0) * (showPercents ? 100 : 1)
              : 0
          )
      }
    }).filter(Boolean) // Remove invalid series

    const optimalColumnWidthPercent = (() => {
      const isWideChart = values[0]?.length > 2
      const minWidth = isWideChart ? 50 : 20
      const totalGroups = values.length
      return minWidth + 60 / (1 + 30 * Math.exp(-totalGroups / 3))
    })()

    const maxYAxisValue = showPercents
      ? 100
      : Math.max(0, ...values.flat().filter(v => v != null)) * 1.1

    const chartOptions: ApexOptions = {
      chart: {
        height: 350,
        type: 'bar',
        toolbar: { show: false },
        parentHeightOffset: 0,
        offsetY: 20,
        animations: { enabled: false }
      },
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (value, { seriesIndex, dataPointIndex }) => {
            if (showPercents) {
              const { value: actualValue, percent } = getValueAndPercent(dataPointIndex, seriesIndex)
              if (percent != null && actualValue != null) {
                return `${actualValue} (${(percent * 100).toFixed(1)}%)`
              } else if (actualValue != null) {
                return `${actualValue}`
              } else {
                return `${value}`
              }
            }
            return `${value}`
          }
        }
      },
      colors: barColors,
      grid: {
        show: true,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } }
      },
      plotOptions: {
        bar: {
          columnWidth: `${optimalColumnWidthPercent}%`,
          dataLabels: { position: 'top' },
          borderRadius: 10,
          borderRadiusApplication: 'end'
        }
      },
      dataLabels: {
        enabled: !(showPercents && values[0]?.length > 1), // Show data labels only if there are 2 or fewer bars
        offsetY: -26,
        style: {
          fontSize: '14px',
          fontWeight: 600,
          colors: [theme.palette.text.secondary]
        },
        formatter: (value, { seriesIndex, dataPointIndex }) => {
          const percent = percentages[dataPointIndex]?.[seriesIndex]
          if (value === 0) {
            return '' // Don't show anything if the value is 0
          }
          return showPercents && percent != null
            ? `${(percent * 100).toFixed(0)}%`
            : `${value}`
        }
      },
      xaxis: {
        categories: keys,
        position: 'top',
        axisBorder: { show: false },
        axisTicks: { show: false },
        offsetY: 26,
        labels: {
          trim: true,
          rotate: 0,
          offsetY: -20,
          style: {
            fontSize: '0.875rem',
            colors: [theme.palette.text.secondary]
          }
        },
        crosshairs: {
          show: false,
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: theme.palette.primary.light,
              colorTo: theme.palette.primary.dark,
              stops: [0, 100],
              opacityFrom: 0,
              opacityTo: 0
            }
          }
        },
        tooltip: { enabled: false }
      },
      yaxis: {
        tickAmount: 4,
        max: maxYAxisValue,
        show: showPercents,
        labels: {
          formatter: value => `${value}${showPercents ? '%' : ''}`
        }
      },
      legend: {
        show: true,
        onItemClick: { toggleDataSeries: true }

      }
    }

    return { options: chartOptions, series }
  }, [report, titles, showPercents, theme])

  return (
    <Card key={chartKey}>
      <CardContent>
        {titleComponent}
        <ReactApexChart
          key={`${chartKey}-${showPercents ? 'pct' : 'raw'}`}
          options={optionsData.options}
          series={optionsData.series}
          type="bar"
          height={350}
        />
        {statisticBox}
      </CardContent>
    </Card>
  )
}

export default BarChart
